import React, { useState } from 'react';
import { ArrowLeft, MapPin, Calendar, Sparkles, ArrowRight } from 'lucide-react';
import SwoopyLogo from '../components/SwoopyLogo';

// All available trips with fixed costs
const ALL_TRIPS = [
  // Budget-friendly trips ($300-500)
  { id: 'b1', destination: 'Bandung', country: 'Indonesia', cost: 280, vibe: 'Chill', vibePercent: 92, duration: '3 days', tags: ['Nature', 'Coffee', 'Cool Weather'] },
  { id: 'b2', destination: 'Yogyakarta', country: 'Indonesia', cost: 320, vibe: 'Explore', vibePercent: 88, duration: '4 days', tags: ['Culture', 'Temples', 'Food'] },
  { id: 'b3', destination: 'Malang', country: 'Indonesia', cost: 350, vibe: 'Balanced', vibePercent: 85, duration: '3 days', tags: ['Mountains', 'Waterfalls', 'Local'] },
  { id: 'b4', destination: 'Chiang Mai', country: 'Thailand', cost: 420, vibe: 'Chill', vibePercent: 90, duration: '4 days', tags: ['Temples', 'Nature', 'Food'] },
  { id: 'b5', destination: 'Hanoi', country: 'Vietnam', cost: 450, vibe: 'Explore', vibePercent: 87, duration: '4 days', tags: ['History', 'Street Food', 'Culture'] },
  
  // Mid-range trips ($500-800)
  { id: 'b6', destination: 'Bali', country: 'Indonesia', cost: 550, vibe: 'Chill', vibePercent: 95, duration: '5 days', tags: ['Beach', 'Wellness', 'Sunset'] },
  { id: 'b7', destination: 'Kuala Lumpur', country: 'Malaysia', cost: 580, vibe: 'Balanced', vibePercent: 87, duration: '4 days', tags: ['Culture', 'Street Food', 'Markets'] },
  { id: 'b8', destination: 'Bangkok', country: 'Thailand', cost: 620, vibe: 'Explore', vibePercent: 91, duration: '5 days', tags: ['Temples', 'Nightlife', 'Food'] },
  { id: 'b9', destination: 'Singapore', country: 'Singapore', cost: 720, vibe: 'Explore', vibePercent: 89, duration: '4 days', tags: ['City', 'Food', 'Shopping'] },
  { id: 'b10', destination: 'Phuket', country: 'Thailand', cost: 750, vibe: 'Chill', vibePercent: 96, duration: '6 days', tags: ['Beach', 'Island', 'Relaxation'] },
  
  // Premium trips ($800-1500)
  { id: 'b11', destination: 'Ho Chi Minh City', country: 'Vietnam', cost: 820, vibe: 'Explore', vibePercent: 93, duration: '5 days', tags: ['History', 'Food', 'Markets'] },
  { id: 'b12', destination: 'Seoul', country: 'South Korea', cost: 950, vibe: 'Balanced', vibePercent: 90, duration: '6 days', tags: ['K-Culture', 'Food', 'Shopping'] },
  { id: 'b13', destination: 'Tokyo', country: 'Japan', cost: 1100, vibe: 'Explore', vibePercent: 94, duration: '7 days', tags: ['Culture', 'Food', 'Tech'] },
  { id: 'b14', destination: 'Osaka', country: 'Japan', cost: 1050, vibe: 'Balanced', vibePercent: 91, duration: '6 days', tags: ['Food', 'Entertainment', 'History'] },
  { id: 'b15', destination: 'Taipei', country: 'Taiwan', cost: 880, vibe: 'Explore', vibePercent: 89, duration: '5 days', tags: ['Night Markets', 'Nature', 'Culture'] },
  { id: 'b16', destination: 'Maldives', country: 'Maldives', cost: 1400, vibe: 'Chill', vibePercent: 98, duration: '5 days', tags: ['Beach', 'Luxury', 'Romance'] },
];

// Filter trips that are <= budget (user's requirement: under budget only, no over-budget)
const generateTripsForBudget = (budget, currency) => {
  // Filter trips where cost is less than or equal to budget
  const filteredTrips = ALL_TRIPS
    .filter(trip => trip.cost <= budget)
    .sort((a, b) => b.vibePercent - a.vibePercent) // Sort by best match first
    .map(trip => ({
      ...trip,
      currency
    }));
  
  return filteredTrips;
};

export default function BudgetResultsPage({ budget, currency, onSelectTrip, onBack }) {
  const trips = generateTripsForBudget(budget, currency);

  const formatCurrency = (amount) => {
    const symbols = { 'USD': '$', 'IDR': 'Rp', 'EUR': '€', 'GBP': '£', 'SGD': 'S$' };
    const symbol = symbols[currency] || '$';
    if (currency === 'IDR') {
      return `${symbol}${(amount * 15000).toLocaleString()}`;
    }
    return `${symbol}${amount.toLocaleString()}`;
  };

  const getVibeColor = (vibe) => {
    switch (vibe) {
      case 'Chill': return { bg: 'rgba(107, 203, 119, 0.15)', text: '#4CAF50' };
      case 'Explore': return { bg: 'var(--coral-soft)', text: 'var(--coral)' };
      case 'Balanced': return { bg: 'rgba(255, 193, 7, 0.15)', text: '#F59E0B' };
      default: return { bg: 'var(--coral-soft)', text: 'var(--coral)' };
    }
  };

  return (
    <div className="min-h-screen pb-6" style={{ backgroundColor: 'var(--bg)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50 px-4 py-3"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div className="max-w-lg mx-auto flex items-center">
          <button 
            onClick={onBack} 
            className="flex items-center gap-2 p-2 -ml-2 rounded-full"
            style={{ color: 'var(--muted)' }}
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4">
        {/* Results Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <SwoopyLogo variant="icon" size={80} />
          </div>
          <h1 className="text-xl font-semibold mb-1" style={{ color: 'var(--text)' }}>
            Trips for {formatCurrency(budget)}
          </h1>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>
            {trips.length} perfect matches found
          </p>
        </div>

        {/* Trip Cards */}
        <div className="space-y-4">
          {trips.length === 0 ? (
            <div className="swoopy-card p-8 text-center">
              <p className="text-lg font-medium mb-2" style={{ color: 'var(--text)' }}>
                No trips found under {formatCurrency(budget)}
              </p>
              <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                Try increasing your budget to see more options
              </p>
              <button 
                onClick={onBack}
                className="swoopy-btn h-10 px-5 text-sm"
              >
                Adjust budget
              </button>
            </div>
          ) : (
            trips.map((trip) => {
              const vibeStyle = getVibeColor(trip.vibe);
              return (
                <div 
                  key={trip.id}
                  className="swoopy-card p-5 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => onSelectTrip(trip)}
                  data-testid={`trip-card-${trip.id}`}
                >
                  {/* Vibe Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <div 
                      className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5"
                      style={{ backgroundColor: vibeStyle.bg, color: vibeStyle.text }}
                    >
                      <Sparkles size={12} />
                      {trip.vibePercent}% {trip.vibe}
                    </div>
                    <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--muted)' }}>
                      <Calendar size={14} />
                      {trip.duration}
                    </div>
                  </div>

                  {/* Destination */}
                  <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--text)' }}>
                    {trip.destination}
                  </h3>
                  <div className="flex items-center gap-1.5 text-sm mb-3" style={{ color: 'var(--muted)' }}>
                    <MapPin size={14} />
                    {trip.country}
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {trip.tags.map((tag, i) => (
                      <span 
                        key={i}
                        className="px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: 'var(--border-color)', color: 'var(--text)' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Cost & CTA */}
                  <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                    <div>
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>Estimated total</p>
                      <p className="text-lg font-bold" style={{ color: 'var(--coral)' }}>
                        {formatCurrency(trip.cost)}
                      </p>
                    </div>
                    <button 
                      className="swoopy-btn h-10 px-5 text-sm gap-1.5"
                      onClick={(e) => { e.stopPropagation(); onSelectTrip(trip); }}
                      data-testid={`view-trip-btn-${trip.id}`}
                    >
                      View trip
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer Note */}
        <p 
          className="text-center text-xs mt-6 px-4"
          style={{ color: 'var(--muted)' }}
        >
          Prices are estimates based on your budget 📍
        </p>
      </div>
    </div>
  );
}
